import React, { useRef } from 'react';
import { ConviteDetalhe, CotacaoDetalhe } from '../../../interfaces/PortalCotacoes';
import Referencias from './Referencias';
// Overlay compartilhado dos modais do sistema + receita visual do modal irmão
// (reuso por import, nunca cópia).
import '../../../components/modal/add-car/AddCarModal.css';
import './FornecedorDetalhesModal.css';
import './PropostaRecebidaModal.css';
import '../pages/PortalCotacoes.css'; // `pc-btn--link` (at-17), por import explícito (BUG-V74)
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Proposta recebida de UM fornecedor (12/08/2026, pedido do Victor: "fulano respondeu,
 * mas respondeu o quê? Cadê seus valores?").
 *
 * O `GET /cotacoes/:id` sempre devolveu a proposta ativa completa dentro do convite — a
 * tela mostrava só "Respondeu em ..." e nenhum valor. Não há regra de sigilo do COMPRADOR
 * antes do encerramento (o sigilo documentado é entre fornecedores), então isto é só dar
 * tela ao dado que já chegava. Abre pelo clique no nome do fornecedor que respondeu —
 * padrão da casa: o nome é o link, como a placa na Recepção.
 *
 * A comparação lado a lado continua sendo a aba "Comparação", depois de encerrar as
 * respostas — aqui é a proposta de UM fornecedor, do jeito que ele enviou.
 */

interface PropostaRecebidaModalProps {
  convite: ConviteDetalhe;
  cotacao: CotacaoDetalhe;
  onFechar: () => void;
  /** Abre o histórico das correções (at-17). Quem chama fecha este modal antes de abrir o outro. */
  onVerHistorico?: () => void;
}

function moeda(valor: string | number | null): string {
  if (valor === null || valor === '') return '—';
  const n = typeof valor === 'number' ? valor : parseFloat(valor);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function dataHora(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

const PropostaRecebidaModal: React.FC<PropostaRecebidaModalProps> = ({ convite, cotacao, onFechar, onVerHistorico }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  // A rota devolve só a proposta ATIVA (versões anteriores ficam SUPERADAS no backend).
  const proposta = convite.propostas[0];

  if (!proposta) return null;

  /*
   * Formas de pagamento marcadas (at-10). Proposta anterior à at-10 chega sem o campo — é o
   * que ela é, e a exibição cai no texto livre de `condicaoPagamento`, como sempre foi.
   */
  const formasPagamento = proposta.formasPagamento ?? [];

  /*
   * Total dos itens cotados (preço × quantidade da cotação). SEM FRETE, como todo total
   * do módulo (decisão de 02/08/2026) — o rótulo avisa.
   */
  /*
   * Com várias ofertas da mesma peça (at-16), o total usa a mais barata de cada uma — somar
   * todas contaria a peça duas vezes. É a mesma regra do total por fornecedor do comparador.
   */
  const maisBarata = new Map<string, number>();
  for (const pi of proposta.itens) {
    if (!pi.cotou || pi.precoUnitario === null) continue;
    const preco = typeof pi.precoUnitario === 'number' ? pi.precoUnitario : parseFloat(pi.precoUnitario);
    if (!Number.isFinite(preco)) continue;
    const atual = maisBarata.get(pi.cotacaoItemId);
    if (atual === undefined || preco < atual) maisBarata.set(pi.cotacaoItemId, preco);
  }
  const total = Array.from(maisBarata.entries()).reduce((soma, [cotacaoItemId, preco]) => {
    const item = cotacao.itens.find((i) => i.id === cotacaoItemId);
    return item ? soma + preco * Number(item.quantidade) : soma;
  }, 0);

  return (
    <div
      className="add-car-modal-overlay"
      onMouseDown={(e) => {
        if (modalRef.current && !modalRef.current.contains(e.target as Node)) onFechar();
      }}
    >
      <div className="fornecedor-modal-content proposta-recebida-modal" ref={modalRef}>
        <div className="fornecedor-modal-header">
          <div className="fornecedor-modal-header-info">
            <h2>{convite.fornecedor.razaoSocial}</h2>
            <span className="fornecedor-modal-subtitulo">
              Proposta enviada em {dataHora(proposta.enviadaEm)}
              {proposta.versao > 1 &&
                (onVerHistorico ? (
                  <>
                    {' · '}
                    {/* at-17: a indicação de correção abre o que o fornecedor mudou (Marlon e Larissa). */}
                    <button type="button" className="pc-btn--link" onClick={onVerHistorico}>
                      correção {proposta.versao}: ver o que mudou
                    </button>
                  </>
                ) : (
                  ` · correção ${proposta.versao}`
                ))}
            </span>
          </div>
          <button className="close-button" onClick={onFechar} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        <div className="fornecedor-modal-body">
          <div className="fornecedor-modal-secao">
            <h3>Itens</h3>
            <div className="proposta-recebida-lista">
              {cotacao.itens.flatMap((item) => {
                // Uma linha por OFERTA (at-16); sem nenhuma cotada, a linha única do "não tem".
                const todas = proposta.itens
                  .filter((p) => p.cotacaoItemId === item.id)
                  .sort((a, b) => (a.variante ?? 0) - (b.variante ?? 0));
                const cotadas = todas.filter((p) => p.cotou);
                const linhas = cotadas.length ? cotadas : [todas[0]];
                return linhas.map((pi, indiceOferta) => {
                const cotou = Boolean(pi && pi.cotou);
                const preco =
                  !pi || pi.precoUnitario === null
                    ? null
                    : typeof pi.precoUnitario === 'number'
                      ? pi.precoUnitario
                      : parseFloat(pi.precoUnitario);
                return (
                  <div
                    key={`${item.id}-${indiceOferta}`}
                    className={`proposta-item-card${cotou ? '' : ' proposta-item-card--sem'}`}
                  >
                    <div className="proposta-item-topo">
                      <div className="proposta-item-desc">
                        <span className="proposta-item-nome">{item.descricao}</span>
                        <span className="proposta-item-meta">
                          {item.codigoProduto} · {Number(item.quantidade)} {item.unidade}
                          {cotadas.length > 1 ? ` · opção ${indiceOferta + 1}` : ''}
                          {pi?.condicao ? ` · ${pi.condicao}` : ''}
                        </span>
                      </div>
                      {cotou ? (
                        <div className="proposta-item-preco">
                          <span className="proposta-item-preco-valor">{moeda(preco)}</span>
                          <span className="proposta-item-preco-rotulo">por {item.unidade}</span>
                        </div>
                      ) : (
                        <span className="proposta-item-nao-tem">Não tem esta peça</span>
                      )}
                    </div>

                    {cotou && pi && (
                      <>
                        {/*
                          Campos rotulados numa grade que se ajusta à largura (`auto-fit`): o
                          prazo e a marca do fornecedor ao lado das referências da casa, para
                          responder "R$ 1.600 é caro ou barato?" sem sair da tela (12/08/2026).
                          Antes tudo isso ficava empilhado e alinhado à direita, num canto do
                          modal — ilegível, com o resto da largura vazio.
                        */}
                        <div className="proposta-item-campos">
                          <div className="proposta-campo">
                            <span className="proposta-campo-rotulo">Prazo de entrega</span>
                            <span className="proposta-campo-valor">
                              {pi.prazoEntregaDias === 0
                                ? 'Pronta entrega'
                                : pi.prazoEntregaDias === null || pi.prazoEntregaDias === undefined
                                  ? '—'
                                  : `${pi.prazoEntregaDias} dia(s)`}
                            </span>
                          </div>
                          <div className="proposta-campo">
                            <span className="proposta-campo-rotulo">Marca / fabricante</span>
                            <span className="proposta-campo-valor">{pi.marcaFabricante || '—'}</span>
                          </div>
                          <Referencias
                            formato="celulas"
                            valorOrcado={item.valorOrcado}
                            mediaCompras={item.mediaCompras}
                            mediaComprasQtd={item.mediaComprasQtd}
                            precoOnline={item.precoOnline}
                            precoOnlineUrl={item.precoOnlineUrl}
                            precoOnlinePesquisadoEm={item.precoOnlinePesquisadoEm}
                            precoOnlineConferir={item.precoOnlineConferir}
                            precoProposta={preco}
                          />
                        </div>

                        {pi.observacaoItem && (
                          <div className="proposta-item-obs">
                            <span className="proposta-campo-rotulo">Observação do fornecedor</span>
                            <span className="proposta-item-obs-texto">{pi.observacaoItem}</span>
                          </div>
                        )}

                        {(pi.fotos?.length ?? 0) > 0 && (
                          <div className="proposta-item-obs">
                            <span className="proposta-campo-rotulo">Fotos da peça</span>
                            <div className="proposta-item-fotos">
                              {pi.fotos!.map((foto) =>
                                foto.url ? (
                                  <a key={foto.id} href={foto.url} target="_blank" rel="noreferrer">
                                    <img src={foto.url} alt="Foto da peça enviada pelo fornecedor" />
                                  </a>
                                ) : (
                                  <span key={foto.id} className="proposta-item-obs-texto">
                                    Foto indisponível
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
                });
              })}
            </div>

            {total > 0 && (
              <div className="proposta-total">
                <div className="proposta-total-texto">
                  <span className="proposta-campo-rotulo">Total dos itens cotados</span>
                  {/*
                    O frete passou a vir na proposta (at-11). Proposta anterior ao campo
                    continua sem ele, e o texto diz isso em vez de sugerir que não há frete —
                    "não informado" e "sem frete" levam a ações opostas.
                  */}
                  <span className="proposta-total-hint">
                    {proposta.frete == null
                      ? 'Frete não informado (proposta anterior ao campo) — confira com o fornecedor.'
                      : `Frete desta proposta: ${moeda(proposta.frete)} · total com frete: ${moeda(total + proposta.frete)}`}
                  </span>
                </div>
                <span className="proposta-total-valor">{moeda(total)}</span>
              </div>
            )}
          </div>

          {(formasPagamento.length > 0 || proposta.condicaoPagamento || proposta.observacao) && (
            <div className="fornecedor-modal-secao">
              <h3>Condições</h3>
              {/*
                EMPILHADO, um por linha e em largura total (pedido do Victor, 12/08/2026 —
                a mesma ordem da tela do fornecedor, onde ele digitou os dois campos). São
                textos livres: lado a lado, cada um ficava numa coluna estreita e alta.
              */}
              <div className="proposta-condicoes">
                {/* at-10: o que o fornecedor MARCOU. O campo livre abaixo virou complemento. */}
                {formasPagamento.length > 0 && (
                  <div className="proposta-item-obs">
                    <span className="proposta-campo-rotulo">Formas de pagamento aceitas</span>
                    <span className="proposta-item-obs-texto">{formasPagamento.join(' · ')}</span>
                  </div>
                )}
                {proposta.condicaoPagamento && (
                  <div className="proposta-item-obs">
                    <span className="proposta-campo-rotulo">Condição de pagamento</span>
                    <span className="proposta-item-obs-texto">{proposta.condicaoPagamento}</span>
                  </div>
                )}
                {proposta.observacao && (
                  <div className="proposta-item-obs">
                    <span className="proposta-campo-rotulo">Observação do fornecedor</span>
                    <span className="proposta-item-obs-texto">{proposta.observacao}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="fornecedor-modal-acoes">
            <button type="button" className="fornecedor-modal-btn" onClick={onFechar}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropostaRecebidaModal;
